import { create } from 'zustand';
import {
  templatesAPI,
  EntryTemplate,
  TemplateCreate,
  TemplateUpdate,
  TemplateApply,
  TimeEntry,
} from '../services/api';

interface TemplateState {
  templates: EntryTemplate[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchTemplates: () => Promise<void>;
  createTemplate: (data: TemplateCreate) => Promise<EntryTemplate>;
  updateTemplate: (id: string, data: TemplateUpdate) => Promise<EntryTemplate>;
  deleteTemplate: (id: string) => Promise<void>;
  applyTemplate: (id: string, data: TemplateApply) => Promise<TimeEntry>;
  getTemplateById: (id: string) => EntryTemplate | undefined;
  getFavoriteTemplates: () => EntryTemplate[];
  clearError: () => void;
}

export const useTemplateStore = create<TemplateState>((set, get) => ({
  templates: [],
  isLoading: false,
  error: null,

  fetchTemplates: async () => {
    set({ isLoading: true, error: null });
    try {
      const templates = await templatesAPI.list();
      set({ templates, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch templates',
        isLoading: false,
      });
    }
  },

  createTemplate: async (data: TemplateCreate) => {
    set({ isLoading: true, error: null });
    try {
      const template = await templatesAPI.create(data);
      set((state) => ({
        templates: [...state.templates, template],
        isLoading: false,
      }));
      return template;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to create template',
        isLoading: false,
      });
      throw error;
    }
  },

  updateTemplate: async (id: string, data: TemplateUpdate) => {
    set({ isLoading: true, error: null });
    try {
      const template = await templatesAPI.update(id, data);
      set((state) => ({
        templates: state.templates.map((t) => (t.id === id ? template : t)),
        isLoading: false,
      }));
      return template;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to update template',
        isLoading: false,
      });
      throw error;
    }
  },

  deleteTemplate: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await templatesAPI.delete(id);
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to delete template',
        isLoading: false,
      });
      throw error;
    }
  },

  applyTemplate: async (id: string, data: TemplateApply) => {
    set({ isLoading: true, error: null });
    try {
      const entry = await templatesAPI.apply(id, data);
      set({ isLoading: false });
      return entry;
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to apply template',
        isLoading: false,
      });
      throw error;
    }
  },

  getTemplateById: (id: string) => {
    return get().templates.find((t) => t.id === id);
  },

  getFavoriteTemplates: () => {
    return get().templates.filter((t) => t.is_favorite);
  },

  clearError: () => set({ error: null }),
}));
